import { MAPPING_DOWNLOAD_TYPES, MappingDownloadType } from '@pubstack/common/src/mappingExport'
import { ScopeId } from '@pubstack/common/src/scope'
import { useAdminSite } from '~/api/admin/admin-site.api'
import { MAPPING_EXPORTS_QUERY_KEYS, useMappingExport, useMappingExports } from '~/api/admin/mapping-export.api'
import { useLazyQuery } from '~/api/api-client'
import { downloadCSVData, downloadZipData } from '~/utils/fileDownload'

export const useSiteSetup = (scopeId: ScopeId, siteId: string) => {
  // API Hooks
  const { data: site, isLoading: isSiteLoading } = useAdminSite(scopeId, siteId)
  const { data: mappingExports, isLoading: isMappingExportsLoading } = useMappingExports(scopeId, siteId)
  const { data: mappingExport, isLoading: isMappingExportLoading, refetch: refetchMappingExport, isRefetching: isMappingExportRefetching } = useMappingExport(scopeId, siteId, mappingExports?.[0]?.id)
  const { fetch: downloadAdUnitsFile, isLoading: isDownloadingAdUnits } = useLazyQuery<Buffer<ArrayBufferLike>>({
    url: `/admin/scopes/${scopeId}/sites/${siteId}/mapping-exports/${mappingExport?.id}/files?type=adunits`,
    keys: [...MAPPING_EXPORTS_QUERY_KEYS, mappingExport?.id ?? '', 'files', 'adunits'],
  })
  const { fetch: downloadBiddersMappingFile, isLoading: isDownloadingBiddersMapping } = useLazyQuery<Buffer<ArrayBufferLike>>({
    url: `/admin/scopes/${scopeId}/sites/${siteId}/mapping-exports/${mappingExport?.id}/files?type=bidders-mapping`,
    keys: [...MAPPING_EXPORTS_QUERY_KEYS, mappingExport?.id ?? '', 'files', 'bidders-mapping'],
  })
  const { fetch: downloadAllFile, isLoading: isDownloadingAll } = useLazyQuery<Buffer<ArrayBufferLike>>({
    url: `/admin/scopes/${scopeId}/sites/${siteId}/mapping-exports/${mappingExport?.id}/files?type=all`,
    keys: [...MAPPING_EXPORTS_QUERY_KEYS, mappingExport?.id ?? '', 'files', 'all'],
  })

  const downloadMappingExport = async (type: MappingDownloadType, fileName: string) => {
    if (!mappingExport?.id || !MAPPING_DOWNLOAD_TYPES.includes(type)) return

    const { data } = type === 'adunits' ? await downloadAdUnitsFile() : type === 'bidders-mapping' ? await downloadBiddersMappingFile() : await downloadAllFile()

    if (!data) throw new Error('No data returned')

    if (type === 'adunits') {
      downloadCSVData(data.toString(), fileName)
    } else {
      downloadZipData(data.toString(), fileName)
    }
  }

  return {
    isInitialLoading: isSiteLoading || isMappingExportsLoading,
    isDownloading: { all: isDownloadingAll, adunits: isDownloadingAdUnits, 'bidders-mapping': isDownloadingBiddersMapping },
    site: site,
    mappingExportLoading: isMappingExportLoading || isMappingExportRefetching,
    mappingExport,
    refetchMappingExport,
    downloadMappingExport,
  }
}
